import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import CreateAppointmentModal from './CreateAppointment';
import '../Appointments/Appointments.css';
import Sidebar from '../Sidebar';
import InternalHeader from '../InternalHeader';
import { FaThList } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const localizer = momentLocalizer(moment);

const AppointmentsUserCalendar = () => {
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [appointments, setAppointments] = useState([]);
    const [rawAppointments, setRawAppointments] = useState([]); // Store original appointment data
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState({ email: '' });
    const navigate = useNavigate();

    // Fetch current user's email
    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const res = await fetch('https://api.hgcdental.clinic/api/profile', {
                    credentials: 'include',
                });
                if (!res.ok) throw new Error('Failed to fetch current user');
                const data = await res.json();
                setCurrentUser({ email: data.email });
            } catch (err) {
                console.error('Failed to fetch current user', err);
            }
        };

        fetchCurrentUser();
    }, []);

    // Fetch appointments for the current user
    const fetchAppointments = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get('https://api.hgcdental.clinic/api/appointments', {
                credentials: 'include',
            });
            if (!response.data) {
                throw new Error('Failed to fetch appointments');
            }

            // Filter appointments for the current user
            const userAppointments = response.data.filter(
                (appointment) => appointment.email === currentUser.email && !appointment.isArchived
            );

            // Store raw appointments for display in upcoming appointment section
            setRawAppointments(userAppointments);

            // Format appointments for the calendar
            const formattedAppointments = userAppointments.map((appointment) => {
                const startString = `${appointment.date.split('T')[0]}T${appointment.startTime}:00`;
                const endString = `${appointment.date.split('T')[0]}T${appointment.endTime}:00`;

                const startDate = new Date(startString);
                const endDate = new Date(endString);

                return {
                    id: appointment._id,
                    patientName: appointment.patientName,
                    title: appointment.reason,
                    start: startDate,
                    end: endDate,
                    allDay: false,
                    status: appointment.status || 'Pending'
                };
            });

            setAppointments(formattedAppointments);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Fetch appointments when the component mounts or when the current user's email changes
    useEffect(() => {
        if (currentUser.email) {
            fetchAppointments();
        }
    }, [currentUser.email]);

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    const handleSelectEvent = (event) => {
        navigate(`/appointment-details/${event.id}`);
    };

    const handleSelectSlot = ({ start, end }) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (start < today) {
            alert("You cannot select a date before today.");
        } else {
            navigate('/create-appointment');
        }
    };

    const handleCancelAppointment = async (appointmentId) => {
        const confirmCancel = window.confirm('Are you sure you want to cancel this appointment?');
        if (!confirmCancel) {
            return;
        }

        try {
            const response = await axios.patch(
                `https://api.hgcdental.clinic/api/appointments/${appointmentId}/cancel`,
                {},
                { withCredentials: true }
            );
            
            if (response.status === 200) {
                alert('Appointment cancelled successfully');
                fetchAppointments(); // Refresh the list
            } else {
                throw new Error('Failed to cancel appointment');
            }
        } catch (error) {
            console.error('Error cancelling appointment:', error);
            alert('Failed to cancel appointment. Please try again.');
        }
    };

    const handleNewAppointmentClick = () => {
        setShowForm(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    // Find the next upcoming appointment
    const upcomingAppointment = rawAppointments
        .filter(appt => {
            const apptDate = new Date(`${appt.date.split('T')[0]}T${appt.startTime}:00`);
            return apptDate > new Date() && appt.status !== 'Cancelled';
        })
        .sort((a, b) => {
            const dateA = new Date(`${a.date.split('T')[0]}T${a.startTime}:00`);
            const dateB = new Date(`${b.date.split('T')[0]}T${b.startTime}:00`);
            return dateA - dateB;
        })[0];

    return (
        <div className={`appointments-page ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
            <InternalHeader moduleName="Appointments" toggleSidebar={toggleSidebar} />
            <div className="appointments-container">
                <Sidebar isSidebarVisible={isSidebarVisible} />
                <div className="data-table-container2">
                    <div className="data-table-header">
                        <a href="/create-appointment-user"><button className='create-button'>New</button></a>
                        {isModalOpen && (
                            <CreateAppointmentModal
                                onClose={handleCloseModal}
                                fetchAppointments={fetchAppointments}
                            />
                        )}
                        <div className="button-container">
                            <a href="/appointments-user">
                                <button className="view-toggle-button1"><FaThList /></button>
                            </a>
                        </div>
                    </div>
                    <div className="appointments-layout">
                        <div className="data-table-container-appointments">
                            {loading ? (
                                <p>Loading appointments...</p>
                            ) : error ? (
                                <p className="error-message">{error}</p>
                            ) : (
                                <Calendar
                                    localizer={localizer}
                                    events={appointments}
                                    startAccessor="start"
                                    endAccessor="end"
                                    style={{ height: 600, margin: '20px', flex: 1 }}
                                    selectable
                                    onSelectEvent={handleSelectEvent}
                                    onSelectSlot={handleSelectSlot}
                                    eventPropGetter={(event) => {
                                        let backgroundColor = '#3174ad';
                                        if (event.status === 'Cancelled') {
                                            backgroundColor = '#dc3545';
                                        } else if (event.status === 'Completed') {
                                            backgroundColor = '#28a745';
                                        } else if (event.status === 'Checked In') {
                                            backgroundColor = '#17a2b8';
                                        }
                                        return { style: { backgroundColor } };
                                    }}
                                />
                            )}
                            {showForm && (
                                <CreateAppointmentModal
                                    onClose={() => setShowForm(false)}
                                    onSubmit={fetchAppointments}
                                    setAppointments={setAppointments}
                                />
                            )}
                        </div>
                        <div className="upcoming-appointment">
                            {upcomingAppointment ? (
                                <div className="upcoming-appointment-card">
                                    <h2>Upcoming Appointment</h2>
                                    <p><b>Patient Name:</b> {upcomingAppointment.patientName}</p>
                                    <p><b>Reason:</b> {upcomingAppointment.reason}</p>
                                    <p><b>Date:</b> {moment(upcomingAppointment.date).format('MMMM Do, YYYY')}</p>
                                    <p><b>Time:</b> {upcomingAppointment.startTime} - {upcomingAppointment.endTime}</p>
                                    <p><b>Dentist:</b> {upcomingAppointment.dentist}</p>
                                    <p><b>Status:</b> <span className={`status-${upcomingAppointment.status?.toLowerCase()}`}>
                                        {upcomingAppointment.status || 'Pending'}
                                    </span></p>
                                    <div className="appointment-actions">
                                        <button 
                                            className="next-status-button"
                                            onClick={() => navigate(`/appointment-details-user/${upcomingAppointment._id}`)}
                                        >
                                            View Details
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <p>No upcoming appointments.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentsUserCalendar;